import type { Embedding, ScoreBreakdown } from '@shared/utils/search';
import { scoreAndFilterByEmbedding, usePrecomputedEmbeddings } from '@shared/utils/search';
import { colorwayIndexRows } from '@apps/card/utils/colorway';
import type { FuseOptions, FuseResultWithScore } from 'fuse.js';
import type Fuse from 'fuse.js';
import { useMemo } from 'react';
import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import { useValuePropsPayload } from '../../useValuePropsPayload';
import type { PropAsset } from '../../propValues.types';
import { isCarouselAsset } from '../../propValues.helpers';
import type { GalleryTemplate, GalleryValueProp } from '../GalleryItem/GalleryItem';
import { SIMILARITY_THRESHOLD } from '@shared/utils/search/scoreAndFilterByEmbedding';
import { useSearchContext } from './SearchContext';
import { useShowSearchScores } from './useShowSearchScores';

export function getThemeIdOrEquivalent(item: GalleryTemplate | GalleryValueProp) {
  if (item.type === 'prop') return `valueprop_${item.telemetryId ?? ''}`;
  return item.themeId;
}

/**
 * Per-template identity used to dedupe ranked results into tiles. A `themeId`
 * can span several templates (e.g. a portrait and a landscape variant of the
 * same theme), so we key on the unique template `id` to keep each one as its
 * own tile. Value props have no `id`, so fall back to their equivalent key.
 *
 * So the group key is typically a `StationeryTemplate.id` — the backend UUID,
 * *not* the `themeId` and not the per-colorway `searchThemeId` that scoring
 * uses. Value props instead key as `valueprop_<telemetryId>`.
 */
function getGroupKey(item: GalleryTemplate | GalleryValueProp): string {
  return item.type === 'template' ? item.id : getThemeIdOrEquivalent(item);
}

/**
 * A gallery item flattened to a single (template × colorway) pair for scoring.
 * `searchThemeId` is the embeddings-index key to score against; `colorwayId`
 * (absent for value props / colorway-less templates) recovers the colorway when
 * results are grouped back to one item per theme.
 */
export type SearchItem = (GalleryTemplate | GalleryValueProp) & { colorwayId?: string; searchThemeId: string };

export const EMPTY_COLORWAY_ORDER: ReadonlyMap<string, ReadonlyArray<string>> = new Map();
export const EMPTY_SEARCH_SCORES: ReadonlyMap<string, ScoreBreakdown> = new Map();

/**
 * Expands each template into one item per colorway, scored against that
 * colorway's own index row (`boldly` + `tomato` → `boldly_tomato`), per
 * `colorwayIndexRows`.
 */
export function expandToColorwayItems(items: ReadonlyArray<GalleryTemplate | GalleryValueProp>, hasIndexRow: (key: string) => boolean): SearchItem[] {
  return items.flatMap((item): SearchItem[] => {
    if (item.type !== 'template') return [{ ...item, searchThemeId: getThemeIdOrEquivalent(item) }];
    return colorwayIndexRows(item.themeId, item.themeJSON?.card?.front?.colorways, hasIndexRow).map(row => ({
      ...item,
      colorwayId: row.colorwayId,
      searchThemeId: row.indexKey,
    }));
  });
}

/**
 * Groups globally-ranked colorway items back to one tile per template,
 * preserving order: a template's rank is its best colorway. Tiles are deduped
 * by unique template `id` (via `getGroupKey`) so a theme with multiple templates
 * — e.g. portrait and landscape variants sharing a `themeId` — keeps each as its
 * own tile. `colorwayOrder` stays keyed by `themeId` (colorways are theme-level,
 * identical across orientations) and accumulates colorwayIds best-first, deduped,
 * so both orientation rows feed one shared swatch order (used to order/preselect
 * swatches). Consumers look it up by `themeId`.
 */
export function groupRankedColorwayItems(
  ranked: ReadonlyArray<SearchItem>,
  /** Debug-only: per-row breakdowns keyed by `searchThemeId`, from `scoreAndFilterByEmbedding`. */
  scoresByRow?: ReadonlyMap<string, ScoreBreakdown>,
): {
  filteredItems: Array<GalleryTemplate | GalleryValueProp>;
  colorwayOrder: ReadonlyMap<string, ReadonlyArray<string>>;
  scoresByItem: ReadonlyMap<string, ScoreBreakdown>;
} {
  const groups = new Map<string, GalleryTemplate | GalleryValueProp>();
  const colorwayOrder = new Map<string, string[]>();
  const scoresByItem = new Map<string, ScoreBreakdown>();

  for (const item of ranked) {
    // Accumulate colorway order per theme (shared across orientations), best-first, deduped.
    const themeKey = getThemeIdOrEquivalent(item);
    const order = colorwayOrder.get(themeKey) ?? [];
    if (item.colorwayId && !order.includes(item.colorwayId)) order.push(item.colorwayId);
    colorwayOrder.set(themeKey, order);

    // Keep one tile per template (best-ranked first); distinct orientations survive.
    const groupKey = getGroupKey(item);
    if (!groups.has(groupKey)) {
      const { colorwayId: _colorwayId, searchThemeId: _searchThemeId, ...rest } = item;
      groups.set(groupKey, rest as GalleryTemplate | GalleryValueProp);

      // `ranked` is best-first, so the first row seen for a group is the one
      // whose score decided the tile's rank.
      const score = scoresByRow?.get(item.searchThemeId);
      if (score) scoresByItem.set(groupKey, score);
    }
  }

  return {
    filteredItems: [...groups.values()],
    colorwayOrder,
    scoresByItem,
  };
}

export function useSearchFilteredGalleryItems(templates: ReadonlyArray<GalleryTemplate>, fuse: Fuse<StationeryTemplate, FuseOptions<StationeryTemplate>>) {
  const { themes, embeddingMap, wellKnownEmbeddings } = usePrecomputedEmbeddings();
  const { activeTerm: term, activeTermEmbedding: termEmbedding } = useSearchContext();
  const isActive = !!termEmbedding;
  const valuePropsData = useValuePropsPayload();
  const showSearchScores = useShowSearchScores();

  const searchResult = useMemo(() => {
    if (!themes) return { filteredItems: templates, colorwayOrder: EMPTY_COLORWAY_ORDER, scoresByItem: EMPTY_SEARCH_SCORES }; // Means still loading
    if (!isActive) return { filteredItems: templates, colorwayOrder: EMPTY_COLORWAY_ORDER, scoresByItem: EMPTY_SEARCH_SCORES };

    /** Injects extra customs for well known tags. */
    const getItemCustomsForGalleryItem = (item: SearchItem): ReadonlyArray<Embedding> => {
      if (item.type !== 'template') return [];

      const itemCustoms: Embedding[] = [];

      if (item.tags.includes('photo')) itemCustoms.push(wellKnownEmbeddings.photo);
      if (item.tags.includes('qrCode')) itemCustoms.push(wellKnownEmbeddings.qrCode);
      if (item.mappedTags?.includes('price:basic')) itemCustoms.push(wellKnownEmbeddings.free);
      if (item.mappedTags?.includes('orientation:landscape')) itemCustoms.push(wellKnownEmbeddings.landscape);
      if (item.mappedTags?.includes('orientation:portrait')) itemCustoms.push(wellKnownEmbeddings.portrait);
      if (item.mappedTags?.includes('foil:with')) itemCustoms.push(wellKnownEmbeddings.foil);

      return itemCustoms;
    };

    // Carousel tiles are pinned promo units with no top-level contentList — exclude them from the
    // search corpus (they'd render nothing and break ValuePropCard's contentList access).
    const valueProps: ReadonlyArray<GalleryValueProp> = (valuePropsData?.props ?? [])
      .filter((prop): prop is PropAsset => !isCarouselAsset(prop))
      .map(prop => ({
        ...prop,
        type: 'prop',
      }));
    const items = [...templates, ...valueProps];

    const expanded = expandToColorwayItems(items, key => themes.has(key));

    const fuseResults = fuse.search(term) as FuseResultWithScore<StationeryTemplate>[];

    const collectScores = showSearchScores ? new Map<string, ScoreBreakdown>() : undefined;

    const ranked = scoreAndFilterByEmbedding(expanded, {
      termEmbedding,
      themes,
      embeddingMap,
      getThemeId: item => item.searchThemeId, // embeddings-index row (colorway or base)
      getLexicalThemeId: getThemeIdOrEquivalent, // name match applies to every colorway row of a theme
      fuseResults,
      getFuseItemThemeId: item => item.themeId, // Fuse name-match is keyed by the real themeId
      searchTerm: term,
      getColorwayId: item => item.colorwayId, // lets a curated `themeId+colorwayId` entry target one colorway
      threshold: SIMILARITY_THRESHOLD,
      getItemCustoms: getItemCustomsForGalleryItem,
      collectScores,
    });

    return groupRankedColorwayItems(ranked, collectScores);
  }, [templates, valuePropsData, fuse, themes, embeddingMap, term, termEmbedding, isActive, wellKnownEmbeddings, showSearchScores]);

  return {
    isActive,
    filteredItems: searchResult.filteredItems,
    colorwayOrder: searchResult.colorwayOrder,
    scoresByItem: searchResult.scoresByItem,
  };
}
